import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Activity, Search, Clock, MapPin, Monitor, Printer, Download, Trash2 } from 'lucide-react';
import jsPDF from 'jspdf';
const roleColor: Record<string, string> = {
  Administrator: 'bg-red-100 text-red-700',
  Reception: 'bg-blue-100 text-blue-700',
  Nursing: 'bg-green-100 text-green-700',
  Clinician: 'bg-indigo-100 text-indigo-700',
  Laboratory: 'bg-yellow-100 text-yellow-700',
  Radiology: 'bg-purple-100 text-purple-700',
  Pharmacy: 'bg-pink-100 text-pink-700',
  'Pharmacy Manager': 'bg-teal-100 text-teal-800'
};
const formatDuration = (loginAt: string, logoutAt?: string) => {
  const start = new Date(loginAt).getTime();
  const end = logoutAt ? new Date(logoutAt).getTime() : Date.now();
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};
export const ActivityLog: React.FC = () => {
  const {
    loginSessions,
    auditEvents,
    deleteLoginSessions,
    deleteLoginSessionsBefore,
    deleteLoginSessionsInRange
  } = useData();
  const [activeTab, setActiveTab] = useState<'logins' | 'audit'>('logins');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Ended'>('All');
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearMode, setClearMode] = useState<'before' | 'range' | 'all'>('before');
  const [clearStart, setClearStart] = useState('');
  const [clearEnd, setClearEnd] = useState('');
  const sortedSessions = useMemo(() => {
    return [...loginSessions].sort((a, b) => new Date(b.loginAt).getTime() - new Date(a.loginAt).getTime());
  }, [loginSessions]);
  const filtered = useMemo(() => {
    return sortedSessions.filter(s => {
      const matchesSearch = s.userName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === 'All' || s.role === roleFilter;
      const matchesStatus = statusFilter === 'All' || statusFilter === 'Active' && !s.logoutAt || statusFilter === 'Ended' && !!s.logoutAt;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [sortedSessions, searchQuery, roleFilter, statusFilter]);
  const activeCount = sortedSessions.filter(s => !s.logoutAt).length;
  const todayCount = sortedSessions.filter(s => new Date(s.loginAt).toDateString() === new Date().toDateString()).length;
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPDF = () => {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });
    const margin = 30;
    let y = margin;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('User Activity Log', margin, y);
    y += 18;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}  ·  ${filtered.length} sessions`, margin, y);
    y += 18;
    // Column headers
    const cols = [{
      label: 'User',
      x: margin,
      w: 110
    }, {
      label: 'Role',
      x: margin + 110,
      w: 70
    }, {
      label: 'Login',
      x: margin + 180,
      w: 110
    }, {
      label: 'Logout',
      x: margin + 290,
      w: 110
    }, {
      label: 'Duration',
      x: margin + 400,
      w: 60
    }, {
      label: 'Location',
      x: margin + 460,
      w: 140
    }, {
      label: 'Device',
      x: margin + 600,
      w: 180
    }];
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    cols.forEach(c => pdf.text(c.label, c.x, y));
    y += 4;
    pdf.setLineWidth(0.5);
    pdf.line(margin, y, margin + 780, y);
    y += 10;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const pageHeight = pdf.internal.pageSize.getHeight() - margin;
    filtered.forEach(s => {
      if (y > pageHeight) {
        pdf.addPage();
        y = margin;
      }
      const row = [s.userName, s.role, new Date(s.loginAt).toLocaleString(), s.logoutAt ? new Date(s.logoutAt).toLocaleString() : '—', formatDuration(s.loginAt, s.logoutAt), s.location || '—', s.device || '—'];
      cols.forEach((c, i) => {
        const text = String(row[i] || '—');
        const lines = pdf.splitTextToSize(text, c.w - 5);
        pdf.text(lines, c.x, y);
      });
      y += 16;
    });
    pdf.save(`activity-log-${new Date().toISOString().slice(0, 10)}.pdf`);
  };
  const openClearModal = () => {
    setClearMode('before');
    setClearStart('');
    setClearEnd('');
    setShowClearModal(true);
  };
  const handleConfirmClear = () => {
    if (clearMode === 'before' && clearStart) {
      const cutoffISO = new Date(clearStart).toISOString();
      const affected = sortedSessions.filter(s => new Date(s.loginAt).getTime() < new Date(cutoffISO).getTime()).length;
      if (affected > 0 && confirm(`Delete ${affected} session(s) logged before ${new Date(cutoffISO).toLocaleDateString()}?`)) {
        deleteLoginSessionsBefore(cutoffISO);
      }
    } else if (clearMode === 'range' && clearStart && clearEnd) {
      const startISO = new Date(clearStart).toISOString();
      const endISO = new Date(clearEnd + 'T23:59:59').toISOString();
      const affected = sortedSessions.filter(s => {
        const t = new Date(s.loginAt).getTime();
        return t >= new Date(startISO).getTime() && t <= new Date(endISO).getTime();
      }).length;
      if (affected > 0 && confirm(`Delete ${affected} session(s) logged between ${new Date(startISO).toLocaleDateString()} and ${new Date(endISO).toLocaleDateString()}?`)) {
        deleteLoginSessionsInRange(startISO, endISO);
      }
    } else if (clearMode === 'all') {
      if (confirm(`Permanently delete ALL ${sortedSessions.length} session record(s)? This cannot be undone.`)) {
        deleteLoginSessions(sortedSessions.map(s => s.id));
      }
    }
    setShowClearModal(false);
  };
  return <div className="space-y-6" data-id="element-132" data-mp="3rntrrm66q8bg">
      <div className="flex justify-between items-start gap-4 flex-wrap print:hidden" data-id="element-133" data-mp="3rn0v5e0xwz71">
        <div data-id="element-134" data-mp="1sbrcko4lg03l">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-135" data-mp="1sbzmc75f6pak">
            User Activity Log
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-136" data-mp="1xau7hfqv0pxu">
            Login and logout history for all personnel
          </p>
        </div>
        <div className="flex gap-2 flex-wrap" data-id="element-137" data-mp="1pscswv44mky0">
          <Button variant="outline" onClick={handlePrint} data-id="element-138" data-mp="1kzjqwf01lqpm">
            <Printer className="w-4 h-4 mr-2" data-id="element-139" data-mp="1g0q94adq3djn" /> Print
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} data-id="element-140" data-mp="1g101u5q5jyt1">
            <Download className="w-4 h-4 mr-2" data-id="element-141" data-mp="1b2oqtro4fi98" /> Download PDF
          </Button>
          <Button variant="outline" onClick={openClearModal} className="text-red-600 hover:bg-red-50" data-id="element-142" data-mp="28pxik4eptnjo">
            <Trash2 className="w-4 h-4 mr-2" data-id="element-143" data-mp="23r2ou7f7meks" /> Clear Log
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-144" data-mp="1ts55rk0jzd96">
        <Card data-id="element-145" data-mp="1trljjr3ve2pc">
          <div className="flex items-center" data-id="element-146" data-mp="1tu7ttyi5awb9">
            <div className="bg-green-100 p-3 rounded-full" data-id="element-147" data-mp="ncb8wtz92zsd">
              <Activity className="w-6 h-6 text-green-600" data-id="element-148" data-mp="n9oqfzatmzvd" />
            </div>
            <div className="ml-4" data-id="element-149" data-mp="ibn67b1nsuib">
              <p className="text-sm text-gray-500" data-id="element-150" data-mp="1813tejgd62sh">Currently Online</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-151" data-mp="1836qfnfxow6p">{activeCount}</p>
            </div>
          </div>
        </Card>
        <Card data-id="element-152" data-mp="z0fdtkbbnudh">
          <div className="flex items-center" data-id="element-153" data-mp="z0xequ8xiwrq">
            <div className="bg-blue-100 p-3 rounded-full" data-id="element-154" data-mp="z28kgzlxqelh">
              <Clock className="w-6 h-6 text-blue-600" data-id="element-155" data-mp="1319069949149" />
            </div>
            <div className="ml-4" data-id="element-156" data-mp="1qrlj0f5wgi9m">
              <p className="text-sm text-gray-500" data-id="element-157" data-mp="1qsmtoij072qt">Logins Today</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-158" data-mp="1vqew46h9y799">{todayCount}</p>
            </div>
          </div>
        </Card>
        <Card data-id="element-159" data-mp="1i02y5qy0dea7">
          <div className="flex items-center" data-id="element-160" data-mp="1hyi7icetefhm">
            <div className="bg-purple-100 p-3 rounded-full" data-id="element-161" data-mp="1m0vbowdwy5ca">
              <Activity className="w-6 h-6 text-purple-600" data-id="element-162" data-mp="1lzbv14ochs9z" />
            </div>
            <div className="ml-4" data-id="element-163" data-mp="29peh945kw40o">
              <p className="text-sm text-gray-500" data-id="element-164" data-mp="29ocuw3j43v3n">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-165" data-mp="2ehzbu7wb6l59">
                {sortedSessions.length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="border-b border-gray-200" data-id="element-166" data-mp="3u99xat0v1p3e">
        <nav className="-mb-px flex space-x-8" data-id="element-167" data-mp="3u6fawvyadu7c">
          <button onClick={() => setActiveTab('logins')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'logins' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-168" data-mp="cs9xjxchwope">
            Login Sessions
          </button>
          <button onClick={() => setActiveTab('audit')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'audit' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-169" data-mp="3bbqn3m2o20fe">
            System Audit Trail
          </button>
        </nav>
      </div>

      {activeTab === 'logins' && <>
          {/* Filters */}
          <Card data-id="element-170" data-mp="1ejqvtbllxlrq">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-171" data-mp="1ek91eyu5tccb">
              <div className="relative" data-id="element-172" data-mp="19lfmd1z85da1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-173" data-mp="19mh9i4tilq84">
                  <Search className="h-5 w-5 text-gray-400" data-id="element-174" data-mp="14t4jprd3ndds" />
                </div>
                <input type="text" placeholder="Search by user name..." className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-175" data-mp="zvcvb7bny9ec" />
              </div>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-176" data-mp="2ga4d6qb6ny9e">
                <option value="All" data-id="element-177" data-mp="2ntndqagh0a5q">All Roles</option>
                <option value="Administrator" data-id="element-178" data-mp="2ns2j6ku5or7v">Administrator</option>
                <option value="Reception" data-id="element-179" data-mp="2sk3rjvo7dxsm">Reception</option>
                <option value="Nursing" data-id="element-180" data-mp="2smpzhwf0afbd">Nursing</option>
                <option value="Clinician" data-id="element-181" data-mp="2xif4if9soof2">Clinician</option>
                <option value="Laboratory" data-id="element-182" data-mp="1zvy6f7t8m6s9">Laboratory</option>
                <option value="Radiology" data-id="element-183" data-mp="1zxj1qzndltr6">Radiology</option>
                <option value="Pharmacy" data-id="element-184" data-mp="24tzq9uyhma4s">Pharmacy</option>
                <option value="Pharmacy Manager" data-id="element-185" data-mp="24wm7kmb6k407">Pharmacy Manager</option>
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-186" data-mp="29w8l6wux7m9j">
                <option value="All" data-id="element-187" data-mp="1q5w6au10rbp2">All Statuses</option>
                <option value="Active" data-id="element-188" data-mp="1q4bdp9y9m2tu">Currently Online</option>
                <option value="Ended" data-id="element-189" data-mp="1l7kxmmmgn78f">Logged Out</option>
              </select>
            </div>
          </Card>

          {/* Sessions table */}
          <Card noPadding data-id="element-190" data-mp="1z5rm5c5iruvf">
            <div className="overflow-x-auto" data-id="element-191" data-mp="1v4zbqfkwvjxk">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-192" data-mp="1v6afwgi9qtp7">
                <thead className="bg-gray-50" data-id="element-193" data-mp="17evbmi6xm2bb">
                  <tr data-id="element-194" data-mp="17fmtyrbiq3jn">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-195" data-mp="12ga9gd84d8k8">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-196" data-mp="1g99qijnjvatw">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-197" data-mp="nqmahineulzo">
                      Login Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-198" data-mp="9m5ft3ix6roa">
                      Logout Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-199" data-mp="eeop3oakr0na">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-200" data-mp="4nalp5fi5gov">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-201" data-mp="xbq1hu6gk319">
                      Device
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-202" data-mp="nda6hg4b89mh">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-203" data-mp="3840xk0xrmnyh">
                  {filtered.length > 0 ? filtered.map(s => <tr key={s.id} className="hover:bg-gray-50" data-id="element-204" data-mp="3ocql7poi15mg">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-205" data-mp="3jj5lnt79c3l5">
                          {s.userName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-206" data-mp="39m9kk8joujqm">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor[s.role] || 'bg-gray-100 text-gray-700'}`} data-id="element-207" data-mp="39jwvlbfij942">
                            {s.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-208" data-mp="17r87umpsi17">
                          {new Date(s.loginAt).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-209" data-mp="2lxgjjxo7llp5">
                          {s.logoutAt ? new Date(s.logoutAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-210" data-mp="11f63idy0gbro">
                          {formatDuration(s.loginAt, s.logoutAt)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700" data-id="element-211" data-mp="1b4fsvm1gzgc3">
                          <div className="flex items-start gap-1.5 max-w-[14rem]" data-id="element-212" data-mp="1g30yk0zhqs7x">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-213" data-mp="1g53o6jwqejc4" />
                            <div data-id="element-214" data-mp="1k6p4ygcdzhrl">
                              <p className="text-sm text-gray-800" data-id="element-215" data-mp="1p377rao5p61a">
                                {s.location || '—'}
                              </p>
                              {s.ipAddress && <p className="text-xs text-gray-400 font-mono" data-id="element-216" data-mp="3qvlk7u7xliab">
                                  {s.ipAddress}
                                </p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700" data-id="element-217" data-mp="3js5czl7gr0xu">
                          <div className="flex items-start gap-1.5 max-w-[14rem]" data-id="element-218" data-mp="3ju8byuqlg4ep">
                            <Monitor className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-219" data-mp="1nk9zdqubxd9" />
                            <span className="text-sm text-gray-800" data-id="element-220" data-mp="3sr8x8sid9mdx">
                              {s.device || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-221" data-mp="6j9abjaopiam">
                          {s.logoutAt ? <Badge variant="default" data-id="element-222" data-mp="fdnofijhams5">Ended</Badge> : <Badge variant="success" data-id="element-223" data-mp="uaxwsj0trcjn">
                              <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full mr-1 align-middle animate-pulse" data-id="element-224" data-mp="ubpubzdzfkck"></span>
                              Online
                            </Badge>}
                        </td>
                      </tr>) : <tr data-id="element-225" data-mp="1cww0ykrnn80d">
                      <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-226" data-mp="yzyytn636446">
                        No login sessions match your filters.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>}

      {activeTab === 'audit' && <Card noPadding data-id="element-227" data-mp="ga9573e27odu">
          <div className="overflow-x-auto" data-id="element-228" data-mp="gaslkfo2a4pr">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-229" data-mp="l7j55cz2rifx">
              <thead className="bg-gray-50" data-id="element-230" data-mp="l5ofjlcuz25i">
                <tr data-id="element-231" data-mp="6btdozb4ed3t">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-232" data-mp="6bbj0743w6y0">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-233" data-mp="qs60vbuycxlm">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-234" data-mp="mogf0u5nwu4a">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-235" data-mp="crley6tqqlnw">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-236" data-mp="31s7ni0msspg">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-237" data-mp="3q7svbgfis0rw">
                {auditEvents.length > 0 ? [...auditEvents].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()).map(event => <tr key={event.id} className="hover:bg-gray-50" data-id="element-238" data-mp="366zmjrebk9ue">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-id="element-239" data-mp="3b2otwgwkyv19">
                          {new Date(event.at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-240" data-mp="3l14k8hnsa09p">
                          <p className="text-sm font-medium text-gray-900" data-id="element-241" data-mp="2nenohgup7uq2">
                            {event.userName}
                          </p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${roleColor[event.userRole] || 'bg-gray-100 text-gray-700'}`} data-id="element-242" data-mp="2se069n6pqbls">
                            {event.userRole}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-243" data-mp="24ltihcf1zcxz">
                          <Badge variant={event.type === 'consultation.edit' ? 'warning' : 'default'} data-id="element-244" data-mp="29lpm3aju92d0">
                            {event.type}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-245" data-mp="jq06ewfjmwqe">
                          {event.patientName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700" data-id="element-246" data-mp="3o3yfdp1coyl8">
                          <p className="font-medium text-gray-900 mb-1" data-id="element-247" data-mp="3o1ucnaf6u5mj">
                            Reason: {event.reason}
                          </p>
                          {event.type === 'consultation.edit' && <details className="mt-2" data-id="element-248" data-mp="30ecemx5bybb4">
                              <summary className="text-xs text-blue-600 cursor-pointer hover:underline" data-id="element-249" data-mp="30dunug1fspal">
                                View Content Changes
                              </summary>
                              <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-250" data-mp="2wvwuc0m05xgj">
                                <div className="p-2 bg-red-50 border border-red-100 rounded" data-id="element-251" data-mp="2rx3omucyjaqx">
                                  <p className="text-xs font-bold text-red-800 mb-1" data-id="element-252" data-mp="2nuyyw6pmqy9k">
                                    Previous Content
                                  </p>
                                  <pre className="text-[10px] text-red-900 whitespace-pre-wrap overflow-x-auto max-h-40" data-id="element-253" data-mp="2dxat8lqhk6cm">
                                    {event.previousContent ? JSON.stringify(JSON.parse(event.previousContent), null, 2) : 'None'}
                                  </pre>
                                </div>
                                <div className="p-2 bg-green-50 border border-green-100 rounded" data-id="element-254" data-mp="1qa2y2wmrudob">
                                  <p className="text-xs font-bold text-green-800 mb-1" data-id="element-255" data-mp="1gmgxryltjchz">
                                    New Content
                                  </p>
                                  <pre className="text-[10px] text-green-900 whitespace-pre-wrap overflow-x-auto max-h-40" data-id="element-256" data-mp="16nrbzuqsx7wg">
                                    {event.newContent ? JSON.stringify(JSON.parse(event.newContent), null, 2) : 'None'}
                                  </pre>
                                </div>
                              </div>
                            </details>}
                        </td>
                      </tr>) : <tr data-id="element-257" data-mp="bkxs0m21sslo">
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-258" data-mp="bkfr3c4fxq7f">
                      No audit events recorded.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </Card>}

      {/* Clear log modal */}
      {showClearModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 print:hidden" data-id="element-259" data-mp="3ossiuh9zlkq4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-260" data-mp="3jur2i43y3zic">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-261" data-mp="1o2uo6higy8z">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-262" data-mp="3tqkm27g30t9m">
                Clear Activity Log
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-263" data-mp="1d1f5j1bouh4n">
                Permanently delete session records. This cannot be undone.
              </p>
            </div>
            <div className="p-6 space-y-4" data-id="element-264" data-mp="24bb6qyo2u2df">
              <div className="space-y-2" data-id="element-265" data-mp="24ccspy6ms99x">
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-id="element-266" data-mp="28cn1filt5dsh">
                  <input type="radio" name="clearMode" checked={clearMode === 'before'} onChange={() => setClearMode('before')} className="mt-1" data-id="element-267" data-mp="28epqo0f4z2w5" />
                  <span data-id="element-268" data-mp="2n94ag36iygbv">
                    <span className="font-medium" data-id="element-269" data-mp="2n9nkkxpxo0fc">
                      Delete sessions logged BEFORE a date
                    </span>
                    <span className="block text-xs text-gray-500" data-id="element-270" data-mp="2w2hea064n2cq">
                      Useful for clearing out old history while keeping recent
                      activity.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-id="element-271" data-mp="36287zzmv5dbr">
                  <input type="radio" name="clearMode" checked={clearMode === 'range'} onChange={() => setClearMode('range')} className="mt-1" data-id="element-272" data-mp="3b09rums45goa" />
                  <span data-id="element-273" data-mp="pdp6n2fp4o4q">
                    <span className="font-medium" data-id="element-274" data-mp="pf04ymqdh3og">
                      Delete sessions within a date range
                    </span>
                    <span className="block text-xs text-gray-500" data-id="element-275" data-mp="kgypebs5nmhq">
                      Removes everything logged between two specific dates
                      (inclusive).
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-id="element-276" data-mp="18c1cae7effvk">
                  <input type="radio" name="clearMode" checked={clearMode === 'all'} onChange={() => setClearMode('all')} className="mt-1" data-id="element-277" data-mp="13dzyo8sqs8rh" />
                  <span data-id="element-278" data-mp="1w12o8bffx95l">
                    <span className="font-medium text-red-600" data-id="element-279" data-mp="3qa99hbuhneqx">
                      Delete ALL session records
                    </span>
                    <span className="block text-xs text-gray-500" data-id="element-280" data-mp="3v8atq03nhk3z">
                      Wipes the entire activity log.
                    </span>
                  </span>
                </label>
              </div>

              {clearMode === 'before' && <div data-id="element-281" data-mp="37jrlfkklp7bw">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-282" data-mp="37ji5kpt9sgjx">
                    Cutoff date
                  </label>
                  <input type="date" value={clearStart} onChange={e => setClearStart(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-283" data-mp="3bmo7xmd9hnl4" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-284" data-mp="10ttp9llczq3j">
                    All sessions logged before this date will be deleted.
                  </p>
                </div>}

              {clearMode === 'range' && <div className="grid grid-cols-2 gap-3" data-id="element-285" data-mp="2gdt23dfrpnrr">
                  <div data-id="element-286" data-mp="2v1nwqzdc627h">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-287" data-mp="2v1xmqmyds9d6">
                      Start date
                    </label>
                    <input type="date" value={clearStart} onChange={e => setClearStart(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-288" data-mp="2q8l0ukl36go4" />
                  </div>
                  <div data-id="element-289" data-mp="38z2o49n5gk23">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-290" data-mp="1jlpd61kj7xs0">
                      End date
                    </label>
                    <input type="date" value={clearEnd} onChange={e => setClearEnd(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-291" data-mp="1eogvs377jxrt" />
                  </div>
                </div>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50" data-id="element-292" data-mp="9uujwjz3pucz">
              <Button variant="outline" onClick={() => setShowClearModal(false)} data-id="element-293" data-mp="etxqw9yn6anl">
                Cancel
              </Button>
              <Button onClick={handleConfirmClear} className="bg-red-600 hover:bg-red-700 text-white" disabled={clearMode === 'before' && !clearStart || clearMode === 'range' && (!clearStart || !clearEnd)} data-id="element-294" data-mp="jrqxv57rr8ox">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-295" data-mp="3jrtn8zzfi6n9" /> Delete Sessions
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};